import { useEffect, useState } from 'react'
import { useToast } from '../context/ToastContext'
import { apiCall } from '../utils/api'
import { trackCreateApiKey } from '../utils/analytics'

export default function ApiKeyManagement() {
  const [apiKeys, setApiKeys] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [keyName, setKeyName] = useState('')
  const [expiryDays, setExpiryDays] = useState('365')
  const [createdKey, setCreatedKey] = useState(null)
  const { showToast } = useToast()

  useEffect(() => {
    loadApiKeys()
  }, [])

  const loadApiKeys = async () => {
    try {
      setLoading(true)
      const response = await apiCall('/v1/api-keys')
      if (response.ok) {
        const data = await response.json()
        setApiKeys(data.apiKeys || [])
      } else {
        throw new Error('Failed to fetch API keys')
      }
    } catch (error) {
      showToast('error', error.message, 'Failed to load API keys')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateKey = async (e) => {
    e.preventDefault()
    if (!keyName.trim()) {
      showToast('error', 'Please enter a key name', 'Error')
      return
    }

    try {
      setIsCreating(true)
      const response = await apiCall('/v1/api-keys', {
        method: 'POST',
        body: JSON.stringify({
          name: keyName.trim(),
          expiresInDays: parseInt(expiryDays),
        }),
      })

      if (response.ok) {
        const result = await response.json()
        setCreatedKey(result.apiKey)
        setKeyName('')
        showToast('success', `API key "${result.apiKey.name}" created successfully`, 'Created')

        // Track API key creation in Google Analytics
        trackCreateApiKey()

        loadApiKeys()
      } else {
        throw new Error('Failed to create API key')
      }
    } catch (error) {
      showToast('error', error.message, 'Error')
    } finally {
      setIsCreating(false)
    }
  }

  const handleDeleteKey = async (keyId) => {
    if (!confirm('Are you sure you want to delete this API key? This cannot be undone.')) return

    try {
      await apiCall(`/v1/api-keys?keyId=${keyId}`, { method: 'DELETE' })
      showToast('success', 'API key deleted successfully', 'Deleted')
      loadApiKeys()
    } catch (error) {
      showToast('error', error.message, 'Error')
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    showToast('success', 'Copied to clipboard!', 'Copied')
  }

  const stats = {
    total: apiKeys.length,
    active: apiKeys.filter(k => k.status === 'active').length,
    expired: apiKeys.filter(k => k.status === 'expired').length,
  }

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading API keys...</div>
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-700 border-2 border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="text-3xl font-bold text-green-800 dark:text-green-300">{stats.active}</div>
          <div className="text-sm text-green-600 dark:text-green-400">Active Keys</div>
        </div>
        <div className="bg-white dark:bg-gray-700 border-2 border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="text-3xl font-bold text-red-800 dark:text-red-300">{stats.expired}</div>
          <div className="text-sm text-red-600 dark:text-red-400">Expired Keys</div>
        </div>
        <div className="bg-white dark:bg-gray-700 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="text-3xl font-bold text-blue-800 dark:text-blue-300">{stats.total}</div>
          <div className="text-sm text-blue-600 dark:text-blue-400">Total Keys</div>
        </div>
      </div>

      {/* Create Form */}
      <div id="create-api-key" className="bg-white dark:bg-gray-700 rounded-lg overflow-hidden shadow scroll-mt-20">
        <button
          onClick={() => {
            setShowCreateForm(!showCreateForm)
            if (!showCreateForm) setCreatedKey(null)
          }}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-600 transition"
        >
          <div className="flex items-center gap-3">
            <div className="bg-green-500 text-white rounded-lg p-2">
              <i className="fas fa-plus"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white">Create New API Key</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Generate a new key for API access</p>
            </div>
          </div>
          <i className={`fas fa-chevron-down text-green-600 transition ${showCreateForm ? 'rotate-180' : ''}`}></i>
        </button>

        {showCreateForm && (
          <div className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-600 p-6">
            <form onSubmit={handleCreateKey} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Key Name *
                  </label>
                  <input
                    type="text"
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                    placeholder="e.g., Production API Key"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Expires In
                  </label>
                  <select
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="180">180 days</option>
                    <option value="365">365 days (1 year)</option>
                  </select>
                </div>
              </div>
              <button
                type="submit"
                disabled={isCreating}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium disabled:bg-gray-400"
              >
                {isCreating ? 'Creating...' : 'Create API Key'}
              </button>
            </form>

            {createdKey && (
              <div className="mt-6 bg-green-50 dark:bg-green-900 border-2 border-green-300 rounded-lg p-6 animate-fade-in">
                <div className="text-green-800 dark:text-green-300 font-bold mb-4 flex items-center">
                  <i className="fas fa-check-circle mr-2"></i>API Key Created Successfully!
                </div>
                <div className="bg-white dark:bg-gray-800 border-2 border-green-200 dark:border-green-700 rounded-lg p-4 mb-4">
                  <div className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 uppercase">Your API Key</div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={createdKey.key}
                      readOnly
                      onClick={(e) => e.target.select()}
                      className="flex-1 text-sm bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 font-mono select-all"
                    />
                    <button
                      onClick={() => copyToClipboard(createdKey.key)}
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg text-sm font-semibold"
                    >
                      <i className="fas fa-copy mr-2"></i>Copy
                    </button>
                  </div>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-400 p-4 rounded">
                  <div className="font-bold text-orange-800 dark:text-orange-200 mb-1">Important Security Notice</div>
                  <div className="text-sm text-orange-700 dark:text-orange-300">
                    This key will only be displayed once. Make sure to copy it now and store it securely.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Keys List */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Your API Keys</h3>
        {apiKeys.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300">
            <i className="fas fa-key text-4xl text-gray-300 mb-4"></i>
            <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">No API keys yet</p>
            <p className="text-sm text-gray-500">Create your first API key using the form above</p>
          </div>
        ) : (
          <div className="space-y-4">
            {apiKeys.map(key => (
              <div key={key.id} className="bg-white dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h4 className="text-lg font-bold text-gray-900 dark:text-white">{key.name}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        key.status === 'active'
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                      }`}>
                        {key.status === 'active' ? '✓ Active' : '✗ Expired'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <div>
                        <span className="font-medium">Created:</span> {new Date(key.created_at).toLocaleDateString()}
                      </div>
                      {key.last_used_at && (
                        <div>
                          <span className="font-medium">Last Used:</span> {new Date(key.last_used_at).toLocaleDateString()}
                        </div>
                      )}
                      {key.expires_at && (
                        <div>
                          <span className="font-medium">Expires:</span> {new Date(key.expires_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteKey(key.id)}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
                  >
                    <i className="fas fa-trash mr-2"></i>Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
